/* The Workforce attendance page's API client and the shapes it returns.
 *
 * The month payload keeps the shape the design's own data file had — days
 * with a by-shift and by-department split, departments, workers — so the
 * page can re-scope it to a run of days or one department without asking
 * the server again. */

import { request } from './api'

export const SK = ['A', 'B', 'C', 'G', 'O'] as const
export type ShiftKey = (typeof SK)[number]
export type ByShift = Record<ShiftKey, number>

/** One department on one day. */
export interface DeptDay {
  /** Present heads. */
  p: number
  /** Present days flagged LATE. */
  l: number
  /** Punch misses: register present, not both punches on the biometric. */
  m: number
  /** The one-punch and wrong-punch part of `m`, flagged by the biometric itself. */
  f: number
  /** Overtime hours, read off the PP / PPP marks. */
  ot: number
  /** Average hours between the two punches. */
  h: number
  /** Present with no shift code on the biometric — counted under O. */
  u: number
  s: ByShift
}

export interface AttDay {
  date: string
  d: number
  dow: string
  present: number
  markedA: number
  late: number
  miss: number
  flagged: number
  short: number
  ot: number
  avgHrs: number
  noShift: number
  byShift: ByShift
  byDept: Record<string, DeptDay>
}

/** One designation within a department, as the requirement sheet lists it. */
export interface AttRole {
  designation: string
  /** null: on the roll, but the sheet does not ask for it. */
  req: number | null
  reqShift: ByShift | null
  active: number
  avgPresent: number
}

export interface AttDept {
  name: string
  /** Heads the requirement sheet asks for; null when it does not name the department. */
  req: number | null
  /** The same, shift by shift. */
  reqShift: ByShift | null
  roles: AttRole[]
  active: number
  avgPresent: number
  attPct: number
  late: number
  latePct: number
  miss: number
  flagged: number
  short: number
  ot: number
  markedA: number
  absentPunched: number
  noShift: number
  /** Present days per shift over the days in the period, one decimal. */
  byShift: ByShift
}

/** One department-and-designation line, over the whole period: what the
 *  sheet asks for against who is coded to it. Sheet lines first, in the
 *  sheet's order; then the roll's that the sheet does not list, with req 0.
 *  The sheet has no designations for staff, so their lines are one per
 *  department with `desig` blank. */
export interface AttDesig {
  dept: string
  desig: string
  req: number
  reqS: Record<Exclude<ShiftKey, 'O'>, number>
  /** Coded to the line with at least one present day in the month. */
  roll: number
  /** Their average present per day. */
  avg: number
}

export interface AttWorker {
  id: string
  name: string
  dept: string
  /** Days present. */
  p: number
  /** Days the register lists the worker, present or absent. */
  listed: number
  /** p over listed, per cent. */
  att: number
  l: number
  /** l over p, per cent. */
  lp: number
  m: number
  f: number
  ot: number
  /** No punch on the biometric on any day of the month. */
  never: boolean
}

/** Which group a month is for. Staff have no register: their mark is the
 *  biometric report's own word for the day, so punch misses and overtime
 *  are 0 for them by construction. */
export type Who = 'worker' | 'staff'

export interface AttMonth {
  empty: false
  month: string
  /** The months on file for this group. */
  months: { month: string; label: string }[]
  who: Who
  /** 'September 2026', or '1–14 Sep 2026' for part of a month. */
  label: string
  mon: string
  monthName: string
  dayCount: number
  calendarDays: number
  /** People of the group with any row in the month. */
  register: number
  /** Those marked present or absent at least once. */
  activeRoll: number
  dormant: number
  /** On the register, with no punch on the biometric on any day. */
  neverPunched: number
  /** The other group — staff for workers, workers for staff — with a row
   *  in the month, and the same by department, largest first. */
  otherOnFile: number
  otherByDept: { name: string; count: number }[]
  /** The file the requirement on file was loaded from; null if none is. */
  requirementSheet: string | null
  requirementLoaded: string | null
  requiredTotal: number
  /** Staff against their own requirement — kept apart from the workers' figures. */
  staff: {
    required: number
    reqByShift: Partial<ByShift>
    onRoll: number
    avgPresent: number
    dayCount: number
    depts: {
      name: string; req: number | null; reqShift: Partial<ByShift> | null
      onRoll: number; avgPresent: number
    }[]
  }
  reqByShift: Partial<ByShift>
  shiftNames: Record<ShiftKey, string>
  otHours: Record<string, number>
  groups: { name: string; req: number; depts: string[] }[]
  desig: AttDesig[]
  reqUnstaffed: { name: string; req: number }[]
  totals: {
    presentDays: number; late: number; miss: number; flagged: number
    short: number; ot: number; markedA: number; absentPunched: number
    noShift: number
  }
  avgPresent: number
  avgHrs: number
  rowCount: number
  /** Biometric rows on days the register has no mark for the worker. */
  unmarkedRows: number
  unmarkedPunched: number
  sourceFile: string | null
  days: AttDay[]
  depts: AttDept[]
  deptShift: Record<string, Partial<Record<ShiftKey, { late: number; heads: number }>>>
  deptRegister: Record<string, { reg: number; active: number; never: number }>
  workers: AttWorker[]
}

export type AttSummary = AttMonth | { empty: true; months: []; month: null; who: Who }

export interface AttLoad {
  id: number
  filename: string | null
  kind: 'register' | 'biometric'
  uploaded_at: string | null
  uploaded_by: string | null
  days: number
  rows: number
  rows_updated: number | null
  first_date: string | null
  last_date: string | null
}

export interface AttPeriod {
  month: string
  label: string
  days: number
  rows: number
  first_date: string
  last_date: string
  loads: AttLoad[]
}

export interface AttUpload {
  ok: boolean
  loaded: {
    filename: string; kind: string; employees: number; employees_added: number
    days: number; first_date: string | null; last_date: string | null
    rows: number; rows_new: number; rows_updated: number; punch_misses: number
  }[]
  failed: { filename: string; error: string }[]
  periods: AttPeriod[]
}

export const attendance = {
  /** A month not on file for the group falls back to its latest. */
  summary: (month?: string, who: Who = 'worker') => {
    const q = new URLSearchParams({ who })
    if (month) q.set('month', month)
    return request<AttSummary>('/attendance/summary?' + q.toString())
  },
  periods: () => request<{ periods: AttPeriod[] }>('/attendance/periods'),
  /** Replaces the manpower requirement with the sheet in `file` (CSV). */
  uploadRequirement: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request<{
      ok: boolean; source: string; rows: number; rows_replaced: number
      workers: number; staff: number; warnings: string[]
    }>('/attendance/requirement', { method: 'POST', body: fd })
  },
  /** `month` is only for a register whose file name does not carry one. */
  upload: async (files: File[], month?: string) => {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f))
    return request<AttUpload>(
      '/attendance/upload' + (month ? `?month=${month}` : ''), { method: 'POST', body: fd })
  },
}
